import React from "react";
import { Link } from "wouter";
import { PublicLayout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { COMPETITOR_PRICING } from "@/lib/competitor-pricing";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  GraduationCap,
  Swords,
  BarChart3,
  Wallet,
  CalendarCheck,
  Star,
  Clock,
  Video,
  Zap,
  TrendingUp,
  CreditCard,
  Quote,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { RevenueCalculator } from "@/components/revenue-calculator";
import { HeroInfographic } from "@/components/hero-infographic";
import { BookDemoModal } from "@/components/book-demo-modal";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: [0.25, 0.4, 0.25, 1] as const },
  }),
};


const stakeholders = [
  {
    id: "owners",
    label: "Gym Owners",
    icon: Building2,
    color: "from-amber-400 to-orange-500",
    ringColor: "ring-amber-400/30",
    bgColor: "bg-amber-400/10",
    textColor: "text-amber-400",
    benefits: [
      {
        icon: Zap,
        title: "Your private lesson business runs itself.",
        description:
          "Booking requests, confirmations, payments, and payouts all happen automatically. No more chasing trainers to log sessions, manually splitting revenue, or reconciling spreadsheets at the end of the month.",
      },
      {
        icon: BarChart3,
        title: "Full visibility across every trainer and dollar.",
        description:
          "One owner dashboard shows every trainer's schedule, every session booked, and every dollar earned across all your locations. See which hours are peak, which trainers are underutilized, and which students are churning before they disappear.",
      },
      {
        icon: TrendingUp,
        title: "Multiple revenue streams, zero extra overhead.",
        description:
          "Single sessions, multi-session packages, monthly memberships — all enforced and tracked from one platform. Unlock video training with Premium for even more revenue. Set your pricing once. Open Mat handles the rest on every transaction.",
      },
    ],
  },
  {
    id: "trainers",
    label: "Your Trainers",
    icon: Swords,
    color: "from-blue-400 to-cyan-400",
    ringColor: "ring-blue-400/30",
    bgColor: "bg-blue-400/10",
    textColor: "text-blue-400",
    benefits: [
      {
        icon: Wallet,
        title: "Happy trainers who get paid on time — every time.",
        description:
          "When your trainers don't have to chase payments or ask students for Venmo, they stay focused and loyal. Open Mat pays their cut automatically the moment a session is marked complete. No friction, no awkwardness.",
      },
      {
        icon: CalendarCheck,
        title: "Scheduling that doesn't land in your lap.",
        description:
          "Your trainers manage their own availability in the app. Students book directly into open slots. No texts to you asking what's free, no last-minute calendar scrambles. You stay out of it.",
      },
      {
        icon: Star,
        title: "Trainers who build their name at your gym.",
        description:
          "Every completed session earns ratings that build trainer reputation on your platform. The stronger your trainers look, the more private sessions your gym books — and the stickier your students become.",
      },
    ],
  },
  {
    id: "students",
    label: "Your Students",
    icon: GraduationCap,
    color: "from-emerald-400 to-green-400",
    ringColor: "ring-emerald-400/30",
    bgColor: "bg-emerald-400/10",
    textColor: "text-emerald-400",
    benefits: [
      {
        icon: Clock,
        title: "Students who actually show up — because they've paid.",
        description:
          "When students book and pay upfront through Open Mat, no-shows drop dramatically. Your trainers don't lose an hour waiting for someone who never texted back. Your mat time gets used.",
      },
      {
        icon: CreditCard,
        title: "Package deals that lock students into your gym.",
        description:
          "Multi-session packages and monthly memberships keep students committed to your gym long-term. The more they pre-purchase, the more often they train — and the harder they are to poach by a competitor.",
      },
      {
        icon: Video,
        title: "Unlock video training with Premium.",
        description:
          "Premium gyms give students the option to book in-person or virtual sessions with your trainers. More format options mean more bookings — including from students who travel, move, or can't make it to the mat every week.",
      },
    ],
  },
];

const testimonials = [
  {
    name: "Marcus Rivera",
    role: "Gym Owner",
    gym: "Rivera Combat Academy",
    quote: "Before Open Mat, I was spending hours every week chasing down payments and juggling trainer schedules in a spreadsheet. Now the entire private lesson side of my business runs on autopilot. Revenue is up 35% since we started.",
    rating: 5,
  },
  {
    name: "Sarah Chen",
    role: "Head Trainer",
    gym: "Pacific MMA",
    quote: "My gym owner pushed us all onto Open Mat and it was the best thing that happened to my coaching career. I stopped losing sessions to last-minute cancellations the moment the cancellation policy started enforcing itself. My income is predictable now.",
    rating: 5,
  },
  {
    name: "James Okafor",
    role: "Gym Owner",
    gym: "Ironside Boxing & Fitness",
    quote: "We run three locations and Open Mat gives me a single dashboard to see every trainer, every session, every dollar across all of them. The multi-session packages alone doubled our student retention.",
    rating: 5,
  },
  {
    name: "Elena Vasquez",
    role: "BJJ Coach",
    gym: "Gracie Flow Academy",
    quote: "As a trainer, seeing my gym owner finally have a real system changed everything. Students show up because they've already paid. I went from 8 private lessons a week to 15 without any scheduling overhead. The whole gym runs tighter.",
    rating: 4,
  },
];


function FourProblemsCard({
  card,
  index,
}: {
  card: {
    num: string;
    tag: string;
    headline: React.ReactNode;
    body: string;
  };
  index: number;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      className="four-problems-card"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "var(--hp-card-hover-bg)" : "var(--hp-card-bg)",
        padding: "44px 40px",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        position: "relative",
        overflow: "hidden",
        transition: "background 0.2s",
        cursor: "default",
      }}
    >
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          height: 2,
          background: "var(--color-primary)",
          transition: "width 0.35s ease",
          width: hovered ? "100%" : "0%",
        }}
      />
      <div
        style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 13,
          letterSpacing: "0.2em",
          color: "var(--hp-text-subtle)",
          marginBottom: 28,
        }}
      >
        {card.num}
      </div>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--color-primary)",
          marginBottom: 14,
        }}
      >
        ▸ {card.tag}
      </div>
      <div
        style={{
          width: 28,
          height: 1,
          background: "var(--hp-text-subtle)",
          marginBottom: 20,
        }}
      />
      <h3
        style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: "clamp(28px, 3.2vw, 38px)",
          fontWeight: 400,
          lineHeight: 0.95,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
          color: "var(--hp-heading)",
          marginBottom: 20,
        }}
      >
        {card.headline}
      </h3>
      <p
        style={{
          fontSize: 14,
          fontWeight: 300,
          fontStyle: "italic",
          color: "var(--hp-text-body)",
          lineHeight: 1.75,
          flex: 1,
        }}
      >
        {card.body}
      </p>
    </motion.div>
  );
}

function TrainerBlock({
  block,
  index,
}: {
  block: {
    num: string;
    tag: string;
    headline: React.ReactNode;
    body: string;
  };
  index: number;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "var(--hp-card-hover-bg)" : "var(--hp-card-bg)",
        padding: "36px 36px",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        position: "relative",
        overflow: "hidden",
        transition: "background 0.2s",
        cursor: "default",
      }}
    >
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          height: 2,
          background: "var(--color-primary)",
          transition: "width 0.35s ease",
          width: hovered ? "100%" : "0%",
        }}
      />
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 11, letterSpacing: "0.22em", color: "var(--hp-text-subtle)", marginBottom: 14 }}>
        {block.num}
      </div>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-primary)", marginBottom: 10 }}>
        ▸ {block.tag}
      </div>
      <div style={{ width: 28, height: 1, background: "var(--hp-text-subtle)", marginBottom: 14 }} />
      <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(26px, 2.8vw, 34px)", fontWeight: 400, lineHeight: 0.95, letterSpacing: "0.02em", textTransform: "uppercase", color: "var(--hp-heading)", marginBottom: 14 }}>
        {block.headline}
      </h3>
      <p style={{ fontSize: 14, fontWeight: 300, fontStyle: "italic", color: "var(--hp-text-body)", lineHeight: 1.75 }}>
        {block.body}
      </p>
    </motion.div>
  );
}

const softwareAppJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Open Mat",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: "Private lesson management software for martial arts gyms. Automate booking, scheduling, payments, and trainer payouts.",
  url: "https://joinopenmat.com",
  offers: [
    {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      name: "Free Plan",
      description: "Everything you need to start managing private lessons. $3 per session platform fee.",
    },
    {
      "@type": "Offer",
      price: "49",
      priceCurrency: "USD",
      name: "Premium Plan",
      description: "For established gyms ready to scale. $1 per session platform fee. Video sessions, multi-location support, white-label branding.",
    },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "4",
    bestRating: "5",
    worstRating: "1",
  },
  featureList: [
    "Automated session booking and scheduling",
    "Integrated payment processing",
    "Automatic trainer payouts and revenue splits",
    "Session packages and memberships",
    "Cancellation policy enforcement",
    "Multi-location support",
    "Video training sessions",
    "Owner analytics dashboard",
  ],
};

export default function Home() {
  const [activeTab, setActiveTab] = useState("owners");
  const active = stakeholders.find((s) => s.id === activeTab)!;
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <PublicLayout>
      <SEOHead
        title="Martial Arts Gym Management Software — Private Lesson Booking & Payments"
        description="Open Mat automates private lesson booking, scheduling, payments, and trainer payouts for martial arts gyms. Free to start. Set up your gym in minutes."
        canonicalPath="/"
        jsonLd={softwareAppJsonLd}
      />

      <section
        className="relative w-full overflow-hidden"
        style={{ minHeight: "100vh", paddingTop: 32 }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(var(--hp-hero-grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--hp-hero-grid-line) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(ellipse 70% 60% at 50% 40%, var(--hp-hero-overlay-start) 0%, var(--hp-hero-overlay-mid) 55%, transparent 100%)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />

        <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-32px)]">
          <motion.div
            initial="hidden"
            animate="visible"
            className="flex flex-col items-center text-center px-8 md:px-16 py-20 pt-12 lg:pt-16"
            style={{ maxWidth: 800 }}
          >
            <motion.p
              variants={fadeUp}
              custom={0}
              style={{
                fontSize: "0.7rem",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "var(--color-primary)",
                marginBottom: 28,
                fontFamily: "'DM Mono', monospace",
              }}
            >
              ▪ FOR MARTIAL ARTS SCHOOL OWNERS
            </motion.p>

            <motion.h1
              variants={fadeUp}
              custom={1}
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "clamp(3.5rem, 7vw, 7rem)",
                lineHeight: 0.92,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                marginBottom: 36,
                color: "var(--hp-heading)",
              }}
            >
              YOU RUN YOUR SCHOOL.{" "}
              <br />
              <span style={{ color: "var(--color-primary)" }}>WE ORGANIZE YOUR PRIVATES.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              custom={2}
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: "clamp(1.1rem, 1.5vw, 1.35rem)",
                lineHeight: 1.55,
                color: "var(--hp-text-muted)",
                maxWidth: 580,
                marginBottom: 48,
              }}
            >
              <strong>After memberships, private lessons should be one of your gym's largest sources of revenue. Unfortunately, they're probably your biggest missed opportunity.</strong> Current CRMs treat personal training as an afterthought. Let us help fix that.
            </motion.p>

            <motion.div
              variants={fadeUp}
              custom={3}
              className="flex flex-col sm:flex-row items-center gap-4"
            >
              <Link href="/register-gym">
                <Button
                  size="lg"
                  className="h-12 px-9 text-sm bg-primary text-primary-foreground hover:bg-primary/90 font-bold shadow-2xl shadow-primary/25 hover:-translate-y-0.5 transition-all gap-2"
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    borderRadius: 4,
                  }}
                >
                  Set Up Your Gym
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Button
                size="lg"
                variant="outline"
                onClick={() => setDemoOpen(true)}
                className="h-12 px-9 text-sm border-white/10 text-white/70 hover:text-white hover:bg-white/5 font-medium"
                style={{
                  fontFamily: "'DM Mono', monospace",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  borderRadius: 4,
                }}
              >
                Book a Demo
              </Button>
            </motion.div>
            <motion.div variants={fadeUp} custom={4} className="mt-2">
              <a
                href="#how"
                style={{
                  color: "var(--hp-text-link)",
                  fontSize: "0.72rem",
                  letterSpacing: "0.08em",
                  textDecoration: "none",
                  borderBottom: "1px solid var(--hp-link-border)",
                  paddingBottom: 2,
                  fontFamily: "'DM Mono', monospace",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--hp-heading-warm)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--hp-text-link)")}
              >
                See how it works ↓
              </a>
            </motion.div>

          </motion.div>
        </div>
      </section>

      <section className="relative z-10" style={{ borderTop: "1px solid var(--hp-section-border)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 40px" }}>
          <HeroInfographic />
        </div>
      </section>

      <section className="relative z-10" style={{ borderTop: "1px solid var(--hp-section-border)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 40px" }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col items-center"
          >
            <h2
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "clamp(1.6rem, 2.5vw, 2.2rem)",
                color: "var(--hp-heading-warm)",
                marginBottom: "0.25rem",
                textAlign: "center",
              }}
            >
              Private Lesson Revenue Calculator
            </h2>
            <p style={{ color: "var(--hp-text-muted)", fontSize: "0.9rem", marginBottom: "2rem", textAlign: "center" }}>
              (enter numbers below)
            </p>
            <RevenueCalculator />
          </motion.div>
        </div>
      </section>

      <section className="relative z-10" style={{ borderTop: "1px solid var(--hp-section-border)" }}>
        <div className="four-problems-section-inner" style={{ maxWidth: 1100, margin: "0 auto", padding: "100px 40px" }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ marginBottom: 64 }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "var(--color-primary)",
                marginBottom: 20,
                fontFamily: "'DM Mono', monospace",
              }}
            >
              ■ What Open Mat fixes
            </div>
            <h2
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "clamp(52px, 7vw, 88px)",
                fontWeight: 400,
                lineHeight: 0.9,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                color: "var(--hp-heading)",
                marginBottom: 0,
              }}
            >
              Four problems.<br />
              <span style={{ color: "var(--color-primary)" }}>One solution.</span>
            </h2>
            <p
              style={{
                marginTop: 20,
                fontSize: 16,
                fontStyle: "italic",
                fontWeight: 300,
                color: "var(--hp-text-body)",
                lineHeight: 1.65,
                maxWidth: 560,
              }}
            >
              Every one of these costs you money right now. Here's exactly how Open Mat closes the gap.
            </p>
          </motion.div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 1,
              background: "var(--hp-grid-gap-bg)",
              border: "0.5px solid var(--hp-subtle-border)",
            }}
            className="four-problems-grid"
          >
            {[
              {
                num: "01 / 04",
                tag: "Mat utilization",
                headline: <>Dead hours are<br /><span style={{ color: "var(--color-primary)" }}>killing your margins.</span></>,
                body: "Your mats sit empty before class, after class, and on weekends — while your rent runs 24/7. Open Mat surfaces available time directly to your members and turns dead hours into booked sessions.",
              },
              {
                num: "02 / 04",
                tag: "Private lessons",
                headline: <>Stop coordinating<br /><span style={{ color: "var(--color-primary)" }}>over text.</span></>,
                body: "Your coaches want to teach. Your members want to train. Open Mat connects them directly — no group chats, no back-and-forth, no missed bookings. One tap to schedule. Done.",
              },
              {
                num: "03 / 04",
                tag: "Payments",
                headline: <>Get paid the moment<br /><span style={{ color: "var(--color-primary)" }}>it's booked.</span></>,
                body: "No invoices. No \"I'll Venmo you after class.\" Payment processes at the time of scheduling and splits automatically between your gym and your coach — based on the terms you set.",
              },
              {
                num: "04 / 04",
                tag: "Cancellation policy",
                headline: <>A text at 9:58<br /><span style={{ color: "var(--color-primary)" }}>doesn't cancel a 10 AM.</span></>,
                body: "Your coach showed up. Your mat was reserved. Open Mat enforces your cancellation policy automatically — so a last-minute bail doesn't cost your trainer a session fee.",
              },
            ].map((card, i) => (
              <FourProblemsCard key={card.num} card={card} index={i} />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            style={{
              marginTop: 64,
              paddingTop: 32,
              borderTop: "0.5px solid var(--hp-subtle-border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            <p
              style={{
                fontSize: 13,
                fontStyle: "italic",
                color: "var(--hp-text-body)",
                maxWidth: 400,
                lineHeight: 1.6,
              }}
            >
              Every one of these problems is happening at your gym right now. Open Mat takes 15 minutes to set up.
            </p>
            <Link href="/register-gym">
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  background: "var(--color-primary)",
                  color: "var(--hp-gold-contrast)",
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 16,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  padding: "14px 28px",
                  textDecoration: "none",
                  border: "none",
                  cursor: "pointer",
                  transition: "opacity 0.15s",
                  whiteSpace: "nowrap",
                }}
              >
                Get early access →
              </span>
            </Link>
          </motion.div>
        </div>
        <style>{`
          @media (max-width: 700px) {
            .four-problems-grid { grid-template-columns: 1fr !important; }
            .four-problems-section-inner { padding: 72px 24px !important; }
            .four-problems-card { padding: 36px 28px !important; }
          }
          @media (max-width: 420px) {
            .four-problems-section-inner { padding: 56px 18px !important; }
            .four-problems-card { padding: 28px 20px !important; }
          }
        `}</style>
      </section>

      <section className="relative z-10" style={{ borderTop: "1px solid var(--hp-section-border)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "100px 40px", textAlign: "center" }} className="free-for-owners-inner">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "var(--color-primary)",
                marginBottom: 20,
                fontFamily: "'DM Mono', monospace",
              }}
            >
              ■ Zero risk for owners
            </div>
            <h2
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "clamp(52px, 7vw, 88px)",
                fontWeight: 400,
                lineHeight: 0.9,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                color: "var(--hp-heading)",
                marginBottom: 0,
              }}
            >
              It's free to<br />
              <span style={{ color: "var(--color-primary)" }}>get started.</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            style={{
              marginTop: 48,
              display: "flex",
              justifyContent: "center",
              gap: 40,
              flexWrap: "wrap",
            }}
            className="free-for-owners-points"
          >
            {[
              {
                headline: "Zero monthly fees",
                body: "No subscription. No setup cost. No hidden charges. You don't pay anything as a gym owner.",
              },
              {
                headline: "Zero platform markup",
                body: "A small processing fee is charged per booking — paid entirely by the student, not you.",
              },
              {
                headline: "Unlimited everything",
                body: "Unlimited trainers, students, and sessions on every plan. Scale without worrying about costs.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.headline}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + i * 0.1 }}
                style={{
                  flex: "1 1 260px",
                  maxWidth: 320,
                  padding: "32px 24px",
                  border: "0.5px solid var(--hp-subtle-border)",
                  background: "var(--hp-subtle-bg)",
                }}
              >
                <h3
                  style={{
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: 26,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "var(--color-primary)",
                    marginBottom: 12,
                  }}
                >
                  {item.headline}
                </h3>
                <p
                  style={{
                    fontSize: 14,
                    color: "var(--hp-text-muted)",
                    lineHeight: 1.65,
                    fontStyle: "italic",
                    margin: 0,
                  }}
                >
                  {item.body}
                </p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            style={{ marginTop: 48 }}
          >
            <Link href="/register-gym">
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  background: "var(--color-primary)",
                  color: "var(--hp-gold-contrast)",
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 18,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  padding: "16px 36px",
                  textDecoration: "none",
                  border: "none",
                  cursor: "pointer",
                  transition: "opacity 0.15s",
                  whiteSpace: "nowrap",
                }}
              >
                Get started today →
              </span>
            </Link>
            <p
              style={{
                marginTop: 16,
                fontSize: 12,
                color: "var(--hp-text-fine)",
                fontFamily: "'DM Mono', monospace",
                letterSpacing: "0.06em",
              }}
            >
              No credit card required. Set up in under 10 minutes.
            </p>
          </motion.div>
        </div>
        <style>{`
          @media (max-width: 700px) {
            .free-for-owners-inner { padding: 72px 24px !important; }
            .free-for-owners-points { gap: 16px !important; }
          }
          @media (max-width: 420px) {
            .free-for-owners-inner { padding: 56px 18px !important; }
          }
        `}</style>
      </section>

      <section id="how" className="py-24 relative z-10" style={{ borderTop: "1px solid var(--hp-section-border)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p
              style={{
                fontSize: "0.62rem",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "var(--color-primary)",
                marginBottom: 16,
                fontFamily: "'DM Mono', monospace",
              }}
            >
              ▪ The Open Mat way
            </p>
            <h2
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "clamp(2.8rem, 4vw, 4.5rem)",
                lineHeight: 0.95,
                color: "var(--hp-heading-warm)",
                maxWidth: 560,
              }}
            >
              Set up your gym in minutes.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Set up your gym",
                description:
                  "Add your trainers, set session rates, define your revenue splits, and configure your cancellation policy. Your gym's booking page is live in minutes — no dev work required.",
              },
              {
                step: "02",
                title: "Trainers get booked",
                description:
                  "Students find available trainers, pick a time slot, and pay upfront. Zero text messages. Zero back-and-forth. Trainers get notified and confirm with one tap.",
              },
              {
                step: "03",
                title: "Everyone gets paid",
                description:
                  "After each session, the gym's cut and the trainer's cut split automatically. No-shows get charged per your policy. You see every dollar in your dashboard.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="group"
                style={{
                  padding: 40,
                  border: "1px solid var(--hp-card-border)",
                  borderRadius: 2,
                  background: "var(--hp-card-step-bg)",
                  transition: "border-color 0.3s",
                  cursor: "default",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--color-primary)")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--hp-card-border)")}
              >
                <div
                  style={{
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: "4rem",
                    color: "var(--hp-card-border)",
                    lineHeight: 1,
                    marginBottom: 24,
                    transition: "color 0.3s",
                  }}
                  className="group-hover:text-primary"
                >
                  {item.step}
                </div>
                <h3
                  style={{
                    fontFamily: "'Instrument Serif', serif",
                    fontSize: "1.3rem",
                    marginBottom: 12,
                    color: "var(--hp-heading-warm)",
                    fontWeight: "normal",
                    textTransform: "none",
                    letterSpacing: "normal",
                  }}
                >
                  {item.title}
                </h3>
                <p
                  style={{
                    fontSize: "0.72rem",
                    lineHeight: 1.65,
                    color: "var(--hp-text-link)",
                    fontFamily: "'DM Mono', monospace",
                  }}
                >
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-display font-bold text-white mb-4">
              One Platform. Everyone Benefits.
            </h2>
            <p className="text-white/50 max-w-xl mx-auto text-lg" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.85rem" }}>
              As a gym owner, when your trainers and students win, you win.
              Here's what Open Mat delivers for each person in your gym.
            </p>
          </motion.div>

          <div className="flex justify-center mb-12">
            <div className="inline-flex p-1.5 rounded-full bg-white/[0.04] border border-white/[0.06]">
              {stakeholders.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveTab(s.id)}
                    className={cn(
                      "flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300",
                      activeTab === s.id
                        ? "bg-white/10 text-white shadow-lg"
                        : "text-white/40 hover:text-white/70"
                    )}
                    style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.72rem", letterSpacing: "0.08em" }}
                  >
                    <Icon className="w-4 h-4" />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {active.benefits.map((b, i) => {
              const BIcon = b.icon;
              return (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 hover:bg-white/[0.04] transition-all duration-300 hover:-translate-y-1"
                >
                  <div
                    className={cn(
                      "w-12 h-12 rounded-xl flex items-center justify-center mb-6",
                      active.bgColor
                    )}
                  >
                    <BIcon className={cn("w-6 h-6", active.textColor)} />
                  </div>
                  <h3 className="text-xl font-display font-bold text-white mb-3 leading-tight normal-case">
                    {b.title}
                  </h3>
                  <p className="text-white/50 leading-relaxed" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.72rem" }}>
                    {b.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-24 relative z-10" style={{ borderTop: "1px solid var(--hp-section-border)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-display font-bold text-white mb-4">
              Trusted by Gym Owners
            </h2>
            <p className="text-white/50 max-w-xl mx-auto" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.8rem" }}>
              Hear from the owners and coaches who stopped texting and started running a real business.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 hover:bg-white/[0.04] transition-all duration-300 hover:-translate-y-1"
              >
                <Quote className="w-8 h-8 text-primary/20 mb-4" />
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: 5 }).map((_, si) => (
                    <Star
                      key={si}
                      className={cn(
                        "w-4 h-4",
                        si < t.rating
                          ? "text-primary fill-primary"
                          : "text-white/10"
                      )}
                    />
                  ))}
                </div>
                <p className="text-white/70 leading-relaxed mb-6" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.72rem" }}>
                  "{t.quote}"
                </p>
                <div className="border-t border-white/[0.06] pt-4">
                  <p className="font-display font-bold text-white text-lg leading-tight">
                    {t.name}
                  </p>
                  <p className="text-primary/80 text-sm font-medium" style={{ fontFamily: "'DM Mono', monospace" }}>
                    {t.role}
                  </p>
                  <p className="text-white/40 text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>{t.gym}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="trainers" className="relative z-10" style={{ borderTop: "1px solid var(--hp-section-border)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "100px 40px" }} className="trainers-section-inner">
          <div className="trainers-inner" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "start" }}>
            <div className="trainers-left" style={{ position: "sticky", top: 40 }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-primary)", marginBottom: 20, fontFamily: "'DM Mono', monospace" }}>
                  ■ For trainers &amp; coaches
                </div>
                <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(52px, 5.5vw, 76px)", fontWeight: 400, lineHeight: 0.91, letterSpacing: "0.02em", textTransform: "uppercase", color: "var(--hp-heading)", marginBottom: 24 }}>
                  Train more.<br />Chase<br /><span style={{ color: "var(--color-primary)" }}>nothing.</span>
                </h2>
                <p style={{ fontSize: 15, fontStyle: "italic", fontWeight: 300, color: "var(--hp-text-body)", lineHeight: 1.7, marginBottom: 36, borderLeft: "2px solid var(--color-primary)", paddingLeft: 16 }}>
                  Open Mat handles the scheduling, the payments, and the awkward conversations — so you can focus on what you actually came here to do.
                </p>
                <Link href="/register-gym">
                  <span
                    style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "var(--color-primary)", color: "var(--hp-gold-contrast)", fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, letterSpacing: "0.14em", textTransform: "uppercase", padding: "14px 28px", textDecoration: "none", transition: "opacity 0.15s", whiteSpace: "nowrap", cursor: "pointer" }}
                  >
                    Join as a trainer →
                  </span>
                </Link>
              </motion.div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 1, background: "var(--hp-grid-gap-bg)", border: "0.5px solid var(--hp-subtle-border)" }}>
              {[
                { num: "01 / 04", tag: "Easy booking", headline: <>No more back-and-forth.<br /><span style={{ color: "var(--color-primary)" }}>Just show up.</span></>, body: "Members request a session. You confirm. That's it. No group chats, no DMs, no negotiating times with the front desk. Open Mat keeps your schedule clean and your calendar full." },
                { num: "02 / 04", tag: "Quick payment", headline: <>Paid at booking.<br /><span style={{ color: "var(--color-primary)" }}>Every time.</span></>, body: "Your rate gets locked in when the session is scheduled — not after. No invoices, no \"I'll get you next time,\" no chasing Venmo requests. The money is yours before you even hit the mat." },
                { num: "03 / 04", tag: "Cancellation policy", headline: <>No awkward<br /><span style={{ color: "var(--color-primary)" }}>conversations.</span></>, body: "Cancellation policy is built in and enforced automatically. If a member bails last minute, you're still covered. Open Mat handles the hard conversation so you never have to." },
                { num: "04 / 04", tag: "Set rates", headline: <>Your rate.<br /><span style={{ color: "var(--color-primary)" }}>Not theirs.</span></>, body: "The gym sets your rate and the client sees it upfront — no haggling, no discounts, no uncomfortable money talk. You never have to justify your price or negotiate mid-session. Just train." },
              ].map((block, i) => (
                <TrainerBlock key={block.num} block={block} index={i} />
              ))}
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 820px) {
            .trainers-inner { grid-template-columns: 1fr !important; gap: 52px !important; }
            .trainers-left { position: static !important; }
            .trainers-section-inner { padding: 72px 24px !important; }
          }
          @media (max-width: 420px) {
            .trainers-section-inner { padding: 56px 18px !important; }
          }
        `}</style>
      </section>

      <section id="students" className="relative z-10" style={{ borderTop: "1px solid var(--hp-section-border)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "100px 40px" }} className="students-section-inner">
          <div className="students-inner" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "start" }}>
            <div className="students-blocks" style={{ display: "flex", flexDirection: "column", gap: 1, background: "var(--hp-grid-gap-bg)", border: "0.5px solid var(--hp-subtle-border)" }}>
              {[
                { num: "01 / 03", tag: "Easy booking", headline: <>Book a session.<br /><span style={{ color: "var(--color-primary)" }}>Not a calendar war.</span></>, body: "No more texting your coach three times to find a time that works. Open your app, pick a coach, pick a slot, confirm. Your session is locked in before you leave the mat." },
                { num: "02 / 03", tag: "Easy payment", headline: <>Leave your wallet<br /><span style={{ color: "var(--color-primary)" }}>at home.</span></>, body: "No cash, no Venmo, no hunting down your coach after class to square up. Payment is handled automatically at booking — so the only thing you need to bring is your A-game." },
                { num: "03 / 03", tag: "Coaching staff", headline: <>Every coach.<br /><span style={{ color: "var(--color-primary)" }}>One tap away.</span></>, body: "See every available coach at your gym, their open slots, and their specialties — all in one place. Want to work on your guard with one coach and your striking with another? Done." },
              ].map((block, i) => (
                <TrainerBlock key={block.num} block={block} index={i} />
              ))}
            </div>

            <div className="students-right" style={{ position: "sticky", top: 40 }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-primary)", marginBottom: 20, fontFamily: "'DM Mono', monospace" }}>
                  ■ For students &amp; members
                </div>
                <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(52px, 5.5vw, 76px)", fontWeight: 400, lineHeight: 0.91, letterSpacing: "0.02em", textTransform: "uppercase", color: "var(--hp-heading)", marginBottom: 24 }}>
                  Book your<br />next private.<br /><span style={{ color: "var(--color-primary)" }}>Right now.</span>
                </h2>
                <p style={{ fontSize: 15, fontStyle: "italic", fontWeight: 300, color: "var(--hp-text-body)", lineHeight: 1.7, marginBottom: 36, borderLeft: "2px solid var(--color-primary)", paddingLeft: 16 }}>
                  No more waiting for your coach to text back. Pick a coach, pick a time, pay, and you're on the mat. Open Mat makes booking a private lesson as easy as ordering food.
                </p>
                <Link href="/explore/gyms">
                  <span
                    style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "var(--color-primary)", color: "var(--hp-gold-contrast)", fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, letterSpacing: "0.14em", textTransform: "uppercase", padding: "14px 28px", textDecoration: "none", transition: "opacity 0.15s", whiteSpace: "nowrap", cursor: "pointer" }}
                  >
                    Find your gym →
                  </span>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 820px) {
            .students-inner { grid-template-columns: 1fr !important; gap: 52px !important; }
            .students-blocks { order: 2; }
            .students-right { order: 1; position: static !important; }
            .students-section-inner { padding: 72px 24px !important; }
          }
          @media (max-width: 420px) {
            .students-section-inner { padding: 56px 18px !important; }
          }
        `}</style>
      </section>

      <section className="relative z-10" style={{ borderTop: "1px solid var(--hp-section-border)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "100px 40px" }} className="competitor-section-inner">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ marginBottom: 64 }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "var(--color-primary)",
                marginBottom: 20,
                fontFamily: "'DM Mono', monospace",
              }}
            >
              ■ How we stack up
            </div>
            <h2
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "clamp(52px, 7vw, 88px)",
                fontWeight: 400,
                lineHeight: 0.9,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                color: "var(--hp-heading)",
                marginBottom: 0,
              }}
            >
              Built for martial arts.<br />
              <span style={{ color: "var(--color-primary)" }}>Not against you.</span>
            </h2>
            <p
              style={{
                marginTop: 20,
                fontSize: 16,
                fontStyle: "italic",
                fontWeight: 300,
                color: "var(--hp-text-body)",
                lineHeight: 1.65,
                maxWidth: 560,
              }}
            >
              General gym software charges you every month whether you use it or not. Open Mat charges nothing — your students pay a small booking fee, you keep the rest.
            </p>
          </motion.div>

          <div className="competitor-layout" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "start" }}>
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "var(--hp-text-subtle)",
                  fontFamily: "'DM Mono', monospace",
                  marginBottom: 32,
                }}
              >
                Monthly cost to the gym owner
              </div>
              {(() => {
                const fees = COMPETITOR_PRICING.map((p) => p.fee);
                const maxFee = fees.length > 0 ? Math.max(...fees) : 1;
                return [
                  { name: "Open Mat", fee: 0, highlight: true },
                  ...COMPETITOR_PRICING.map((c) => ({ ...c, highlight: false })),
                ].map((c, i) => (
                <motion.div
                  key={c.name}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12 }}
                  style={{ marginBottom: 28 }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      marginBottom: 10,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        fontSize: 12,
                        letterSpacing: "0.1em",
                        color: c.highlight ? "var(--color-primary)" : "var(--hp-text-muted)",
                        fontWeight: c.highlight ? 700 : 400,
                      }}
                    >
                      {c.name}
                    </span>
                    <span
                      style={{
                        fontFamily: "'Bebas Neue', sans-serif",
                        fontSize: 20,
                        letterSpacing: "0.04em",
                        color: c.highlight ? "var(--color-primary)" : "var(--hp-text-body)",
                      }}
                    >
                      {c.fee === 0 ? "FREE" : `$${c.fee}/mo`}
                    </span>
                  </div>
                  <div
                    style={{
                      height: 8,
                      background: "var(--hp-card-bg)",
                      border: "0.5px solid var(--hp-subtle-border)",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: c.fee === 0 ? "3%" : `${(c.fee / maxFee) * 100}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: i * 0.12 + 0.2, ease: [0.25, 0.4, 0.25, 1] }}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: "100%",
                        background: c.highlight
                          ? "var(--color-primary)"
                          : "rgba(255,255,255,0.18)",
                      }}
                    />
                  </div>
                  {c.highlight && (
                    <p
                      style={{
                        marginTop: 6,
                        fontSize: 10,
                        fontFamily: "'DM Mono', monospace",
                        letterSpacing: "0.1em",
                        color: "var(--hp-text-subtle)",
                        fontStyle: "italic",
                      }}
                    >
                      $1/session on Premium · $3/session on Free (paid by student)
                    </p>
                  )}
                </motion.div>
              ));
              })()}

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                style={{
                  marginTop: 8,
                  padding: "16px 20px",
                  border: "0.5px solid var(--color-primary)",
                  background: "color-mix(in srgb, var(--color-primary) 6%, transparent)",
                  fontSize: 13,
                  fontStyle: "italic",
                  color: "var(--hp-text-body)",
                  lineHeight: 1.6,
                }}
              >
                {(() => {
                  const fees = COMPETITOR_PRICING.map((p) => p.fee);
                  const lo = fees.length > 0 ? Math.min(...fees) : 0;
                  const hi = fees.length > 0 ? Math.max(...fees) : 0;
                  const range = lo === hi ? `$${lo}` : `$${lo}–$${hi}`;
                  return <>At 25 sessions/month on Premium, Open Mat costs <strong style={{ color: "var(--color-primary)" }}>$25 in student-paid fees</strong> vs {range}/month billed directly to you on competitors — with no booking automation included.</>;
                })()}
              </motion.div>
            </div>

            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "var(--hp-text-subtle)",
                  fontFamily: "'DM Mono', monospace",
                  marginBottom: 32,
                }}
              >
                Feature comparison
              </div>

              <div
                style={{
                  border: "0.5px solid var(--hp-subtle-border)",
                  background: "var(--hp-card-bg)",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 80px 96px 80px",
                    borderBottom: "0.5px solid var(--hp-subtle-border)",
                  }}
                >
                  {["", "Open Mat", "Zen Planner", "Mindbody"].map((h, i) => (
                    <div
                      key={h + i}
                      style={{
                        padding: i === 0 ? "12px 16px" : "12px 8px",
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        fontFamily: "'DM Mono', monospace",
                        color: i === 1 ? "var(--color-primary)" : "var(--hp-text-subtle)",
                        textAlign: i === 0 ? "left" : "center",
                        background: i === 1 ? "color-mix(in srgb, var(--color-primary) 5%, transparent)" : "transparent",
                      }}
                    >
                      {h}
                    </div>
                  ))}
                </div>

                {[
                  { feature: "No monthly owner fee", openMat: true, zenPlanner: false, mindbody: false },
                  { feature: "Private lesson booking", openMat: true, zenPlanner: true, mindbody: true },
                  { feature: "Automated trainer payouts", openMat: true, zenPlanner: false, mindbody: false },
                  { feature: "Cancellation policy enforcement", openMat: true, zenPlanner: false, mindbody: false },
                  { feature: "Session packages & memberships", openMat: true, zenPlanner: true, mindbody: true },
                  { feature: "Revenue split configuration", openMat: true, zenPlanner: false, mindbody: false },
                  { feature: "Martial-arts-first UX", openMat: true, zenPlanner: false, mindbody: false },
                  { feature: "Video sessions", openMat: true, zenPlanner: false, mindbody: false },
                ].map((row, ri, arr) => (
                  <motion.div
                    key={row.feature}
                    initial={{ opacity: 0, x: 8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: ri * 0.06 }}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 80px 96px 80px",
                      borderBottom: ri < arr.length - 1 ? "0.5px solid var(--hp-subtle-border)" : "none",
                    }}
                  >
                    {[row.feature, row.openMat, row.zenPlanner, row.mindbody].map((val, ci) => (
                      <div
                        key={ci}
                        style={{
                          padding: ci === 0 ? "11px 16px" : "11px 8px",
                          fontSize: ci === 0 ? 12 : 15,
                          fontFamily: ci === 0 ? "'DM Mono', monospace" : undefined,
                          color: ci === 0 ? "var(--hp-text-muted)" : undefined,
                          letterSpacing: ci === 0 ? "0.04em" : undefined,
                          textAlign: ci === 0 ? "left" : "center",
                          background: ci === 1 ? "color-mix(in srgb, var(--color-primary) 5%, transparent)" : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: ci === 0 ? "flex-start" : "center",
                        }}
                      >
                        {ci === 0
                          ? val
                          : val
                            ? <span style={{ color: "#4ade80", fontSize: 15, lineHeight: 1 }}>✓</span>
                            : <span style={{ color: "rgba(255,255,255,0.15)", fontSize: 13, lineHeight: 1 }}>✕</span>
                        }
                      </div>
                    ))}
                  </motion.div>
                ))}
              </div>

              <p
                style={{
                  marginTop: 12,
                  fontSize: 10,
                  fontFamily: "'DM Mono', monospace",
                  color: "var(--hp-text-subtle)",
                  letterSpacing: "0.06em",
                  fontStyle: "italic",
                }}
              >
                Competitor features based on publicly available plan information. Pricing as of 2025.
              </p>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 820px) {
            .competitor-layout { grid-template-columns: 1fr !important; gap: 56px !important; }
            .competitor-section-inner { padding: 72px 24px !important; }
          }
          @media (max-width: 420px) {
            .competitor-section-inner { padding: 56px 18px !important; }
          }
        `}</style>
      </section>

      <section className="py-24 relative z-10">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent p-12 md:p-16 text-center overflow-hidden"
            style={{ borderRadius: 2 }}
          >
            <div className="absolute top-[-50%] left-[-30%] w-[80%] h-[120%] rounded-full bg-primary/5 blur-[80px]" />
            <div className="absolute bottom-[-40%] right-[-20%] w-[60%] h-[80%] rounded-full bg-blue-500/5 blur-[60px]" />

            <div className="relative z-10">
              <p
                style={{
                  fontSize: "0.62rem",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: "var(--color-primary)",
                  marginBottom: 20,
                  fontFamily: "'DM Mono', monospace",
                }}
              >
                ▪ It costs you nothing
              </p>
              <h2
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: "clamp(2.5rem, 4vw, 4rem)",
                  lineHeight: 0.95,
                  color: "var(--hp-heading-warm)",
                  marginBottom: 20,
                }}
              >
                You pay nothing.
                <br />
                <span style={{ color: "var(--color-primary)" }}>Your gym runs better.</span>
              </h2>
              <p className="text-white/50 max-w-lg mx-auto mb-10" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.78rem", lineHeight: 1.65 }}>
                Zero monthly fees. Zero setup costs. The only fee is a small processing charge per booking — paid by the student, not you.
                Set up your gym in under 10 minutes.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link href="/register-gym">
                  <Button
                    size="lg"
                    className="h-12 px-9 text-sm bg-primary text-primary-foreground hover:bg-primary/90 font-bold shadow-2xl shadow-primary/25 hover:-translate-y-0.5 transition-all gap-2"
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      borderRadius: 4,
                    }}
                  >
                    Set Up My Gym
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link href="/pricing">
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-12 px-9 text-sm border-white/10 text-white/70 hover:text-white hover:bg-white/5 font-medium"
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      borderRadius: 4,
                    }}
                  >
                    Compare Plans
                  </Button>
                </Link>
              </div>
              <div className="mt-8 flex items-center justify-center gap-6 text-sm text-white/30" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.06em" }}>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400/60" />
                  No credit card required
                </span>
                <span>·</span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400/60" />
                  Free for gym owners
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <BookDemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </PublicLayout>
  );
}
